import { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  CheckCircle2,
  ArrowLeft,
  Loader2,
  Calendar as CalendarIcon,
  Clock,
  User,
  Building2,
  CheckCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { format, addDays, startOfToday, parseISO } from "date-fns";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { PreQualForm, QualificationData } from "@/components/PreQualForm";

const LOCATION_ID = "nsp8u9BgTJB40BULOWfw";
const CALENDAR_ID = "hekL8PnpiU8bb86MC7hq";
const VIBE_API_URL = "https://backend.leadconnectorhq.com/vibe-ai";

const BookDemo = () => {
  const { toast } = useToast();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const [slotsData, setSlotsData] = useState<
    Record<string, { slots: string[] }>
  >({});
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [qualCompleted, setQualCompleted] = useState(false);
  const [qualData, setQualData] = useState<QualificationData | null>(null);
  const [qualScore, setQualScore] = useState<string>("");
  const [qualFlags, setQualFlags] = useState<string[]>([]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    companyName: "",
    notes: "",
  });

  useEffect(() => {
    const saved = localStorage.getItem("cm_qual_completed");
    if (saved === "true") {
      setQualCompleted(true);
      const savedData = localStorage.getItem("cm_qual_data");
      if (savedData) {
        setQualData(JSON.parse(savedData));
      }
      const savedScore = localStorage.getItem("cm_qual_score");
      if (savedScore) setQualScore(savedScore);
      const savedFlags = localStorage.getItem("cm_qual_flags");
      if (savedFlags) setQualFlags(JSON.parse(savedFlags));
    }

    const fetchSlots = async () => {
      setLoadingSlots(true);
      try {
        const start = startOfToday();
        const end = addDays(start, 31);
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await fetch(
          `https://backend.leadconnectorhq.com/calendars/${CALENDAR_ID}/free-slots?startDate=${start.getTime()}&endDate=${end.getTime()}&timezone=${tz}`,
        );

        if (!res.ok) throw new Error("Failed to fetch slots");

        const data = await res.json();
        setSlotsData(data);
      } catch (error) {
        console.error("Failed to fetch slots", error);
        toast({
          title: "Error",
          description:
            "Failed to load availability. Please try refreshing the page.",
          variant: "destructive",
        });
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchSlots();
  }, [toast]);

  const handleQualComplete = (
    data: QualificationData,
    score: string,
    flags: string[],
  ) => {
    setQualData(data);
    setQualScore(score);
    setQualFlags(flags);
    setQualCompleted(true);
    localStorage.setItem("cm_qual_completed", "true");
    localStorage.setItem("cm_qual_score", score);
    localStorage.setItem("cm_qual_flags", JSON.stringify(flags));

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  const availableDates = useMemo(() => {
    return Object.keys(slotsData).filter(
      (date) => slotsData[date]?.slots?.length > 0,
    );
  }, [slotsData]);

  const isDateDisabled = (date: Date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return (
      date < startOfToday() ||
      date > addDays(startOfToday(), 31) ||
      !availableDates.includes(dateStr)
    );
  };

  const selectedDateStr = selectedDate
    ? format(selectedDate, "yyyy-MM-dd")
    : null;
  const availableSlotsForDate = selectedDateStr
    ? slotsData[selectedDateStr]?.slots || []
    : [];

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot) return;

    setSubmitting(true);
    try {
      const sessionId = crypto.randomUUID();

      const res = await fetch(`${VIBE_API_URL}/booking/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          locationId: LOCATION_ID,
          calendarId: CALENDAR_ID,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          companyName: formData.companyName,
          notes: formData.notes,
          selectedSlot,
          selectedTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          sessionId,
          customFields: qualData
            ? [
                {
                  id: "DvDmRmzxTuFjJ4RmzGGX",
                  key: "contact.qualifier_primary_service",
                  field_value: qualData.primaryService,
                },
                {
                  id: "34DcxtnqEEUue4rycUEW",
                  key: "contact.qualifier_other_services",
                  field_value: qualData.otherServices.join(", "),
                },
                {
                  id: "XkhZJvf6NUiJKfFSUnsK",
                  key: "contact.qualifier_other_services_text",
                  field_value: qualData.otherServicesText,
                },
                {
                  id: "zD8JAPEfkeTuLEumvxZU",
                  key: "contact.qualifier_town",
                  field_value: qualData.town,
                },
                {
                  id: "Pwt7L83dQfDn0tG47SUo",
                  key: "contact.qualifier_county",
                  field_value: qualData.county,
                },
                {
                  id: "zwK5r9fPnQmMKtdljMyX",
                  key: "contact.qualifier_van_count",
                  field_value: String(qualData.vanCount),
                },
                {
                  id: "EfN6Y8yoYbFC7rODl7co",
                  key: "contact.qualifier_customer_count",
                  field_value: String(qualData.customerCount),
                },
                {
                  id: "IRJw1Hz24yQWW4NIz1IE",
                  key: "contact.qualifier_yearly_round_value",
                  field_value: qualData.yearlyRoundValue,
                },
                {
                  id: "czwPCc7y4GpxxYexQItL",
                  key: "contact.qualifier_current_marketing",
                  field_value: qualData.currentMarketing.join(", "),
                },
                {
                  id: "MGUO54hGHn17KZHkn3pR",
                  key: "contact.qualifier_agency_history",
                  field_value: qualData.agencyHistory,
                },
                {
                  id: "r2zoLKXjpmKvXoftLKjo",
                  key: "contact.qualifier_current_ad_spend",
                  field_value: qualData.currentAdSpend,
                },
                {
                  id: "tsSu9NA66pffkyQKPWhL",
                  key: "contact.qualifier_future_ad_budget",
                  field_value: qualData.futureAdBudget,
                },
                {
                  id: "5ZxlLIl6d4ph6J7KVcGy",
                  key: "contact.qualifier_12_month_goal",
                  field_value: qualData.twelveMonthGoal,
                },
                {
                  id: "fxSCssUnYDSiEVe6s5xh",
                  key: "contact.qualifier_score",
                  field_value: qualScore,
                },
              ]
            : [],
          tags: qualFlags.length > 0 ? qualFlags : undefined,
        }),
      });

      if (!res.ok) throw new Error("Booking failed");

      // Form tracking integration
      const trackingPayload = {
        type: "external_form_submission",
        timestamp: Date.now(),
        formId: "Book Demo Form",
        formData: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          organization: formData.companyName,
        },
        formLabels: {
          first_name: "First Name",
          last_name: "Last Name",
          email: "Email",
          phone: "Phone",
          organization: "Company Name",
        },
        url: window.location.href,
        title: document.title,
        path: window.location.pathname,
        userAgent: navigator.userAgent,
        trackingId: "tk_4228c869be4b43a6af25f3931923e54d",
        locationId: "nsp8u9BgTJB40BULOWfw",
        sessionId,
        properties: {
          deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
            ? "mobile"
            : "desktop",
        },
      };

      fetch("https://backend.leadconnectorhq.com/external-tracking/events", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          version: "2021-07-28",
        },
        body: JSON.stringify(trackingPayload),
      }).catch(() => {}); // Fire-and-forget

      // Send data to the requested webhook
      fetch(
        "https://services.leadconnectorhq.com/hooks/nsp8u9BgTJB40BULOWfw/webhook-trigger/bbe386c0-ad3a-4ae2-b91b-3c50364f008a",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            companyName: formData.companyName,
            notes: formData.notes,
            selectedSlot,
            qualData,
            qualScore,
            qualFlags,
          }),
        },
      ).catch(() => {}); // Fire-and-forget

      setStep(3);
    } catch (error) {
      toast({
        title: "Booking Failed",
        description:
          "There was an error booking your appointment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-foreground flex flex-col">
      <SEOHead
        title="Book a Demo | Clean Marketing - Window Cleaning Marketing"
        description="Book a free 60-minute discovery call with Clean Marketing and see how our automated quoting and booking system fills your vans with regular window cleaning customers."
        canonical="/book-demo"
      />
      <Header />

      <main className="flex-grow container mx-auto px-4 py-12 lg:py-20 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Left Column: Value Prop */}
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">
                <span className="text-black">See</span>{" "}
                <span className="text-primary">Clean</span>{" "}
                <span className="text-secondary">Marketing</span>{" "}
                <span className="text-black">in action</span>
              </h1>
              <p className="text-lg text-slate-600">
                Book a free 60-minute discovery call to see exactly how we can
                help you add recurring revenue to your window cleaning business.
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-secondary text-lg">
                What we'll cover:
              </h3>
              {[
                "How our automated quoting system works",
                "A look inside the CRM and financial dashboard",
                "Live examples of successful local ad campaigns",
                "Pricing and ROI expectations for your specific area",
              ].map((item, i) => (
                <div key={i} className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mr-3" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <Card className="bg-white border-slate-200 shadow-sm hidden lg:block">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                    <img
                      src="https://vibe.filesafe.space/1775166810156919871/attachments/abf2c69d-9faf-47e1-9eec-bedfb43b7d11.png"
                      alt="Farras Window Cleaning Logo"
                      className="w-full h-full object-contain p-2"
                    />
                  </div>
                  <div>
                    <p className="font-bold text-secondary">Will Farras</p>
                    <p className="text-sm text-slate-500">
                      Farras Window Cleaning
                    </p>
                  </div>
                </div>
                <p className="text-slate-600 italic">
                  "Clean Marketing doubled our round to £160,000 in just 6
                  months. The automated system saves me hours of admin work
                  every single week."
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Calendar Booking Widget */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 flex flex-col overflow-hidden">
            {!qualCompleted ? (
              <div className="p-4 sm:p-6 lg:p-10">
                <PreQualForm onComplete={handleQualComplete} />
              </div>
            ) : (
              <div className="p-6 lg:p-8">
                {step === 1 && (
                  <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <h3 className="text-xl font-bold text-secondary mb-6">
                      Select a Date & Time
                    </h3>

                    {loadingSlots ? (
                      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
                        <Loader2 className="h-8 w-8 animate-spin mb-4 text-primary" />
                        <p>Loading availability...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-6">
                        <div className="flex-shrink-0 mx-auto md:mx-0">
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={handleDateSelect}
                            disabled={isDateDisabled}
                            className="rounded-md border shadow-sm p-3 bg-white"
                            fromDate={startOfToday()}
                            toDate={addDays(startOfToday(), 31)}
                          />
                        </div>

                        <div className="flex-1 flex flex-col min-w-0">
                          <h4 className="font-medium text-slate-700 mb-4 flex items-center">
                            <Clock className="w-4 h-4 mr-2 shrink-0" />
                            {selectedDate
                              ? format(selectedDate, "EEEE, MMMM d")
                              : "Select a date"}
                          </h4>

                          <div className="flex-grow overflow-y-auto pr-2 max-h-[300px] space-y-2">
                            {!selectedDate ? (
                              <p className="text-sm text-slate-500 text-center py-10 bg-slate-50 rounded-lg border border-dashed">
                                Choose a date to see available times.
                              </p>
                            ) : availableSlotsForDate.length === 0 ? (
                              <p className="text-sm text-slate-500 text-center py-10 bg-slate-50 rounded-lg border border-dashed">
                                No slots available for this date.
                              </p>
                            ) : (
                              availableSlotsForDate.map((slot) => {
                                const timeString = format(
                                  parseISO(slot),
                                  "h:mm a",
                                );
                                return (
                                  <Button
                                    key={slot}
                                    variant={
                                      selectedSlot === slot
                                        ? "default"
                                        : "outline"
                                    }
                                    className={`w-full justify-center ${selectedSlot === slot ? "bg-primary text-primary-foreground hover:bg-primary/90" : "hover:border-primary hover:text-primary"}`}
                                    onClick={() => setSelectedSlot(slot)}
                                  >
                                    {timeString}
                                  </Button>
                                );
                              })
                            )}
                          </div>

                          {selectedSlot && (
                            <div className="mt-6 pt-4 border-t">
                              <Button
                                className="w-full bg-secondary hover:bg-secondary/90 text-white"
                                onClick={() => setStep(2)}
                              >
                                Continue to Details
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {step === 2 && (
                  <div className="animate-in fade-in slide-in-from-right-4 duration-500">
                    <div className="flex items-center mb-6">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setStep(1)}
                        className="mr-2 -ml-2 text-slate-500"
                      >
                        <ArrowLeft className="h-4 w-4 mr-1" /> Back
                      </Button>
                      <h3 className="text-xl font-bold text-secondary">
                        Your Details
                      </h3>
                    </div>

                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 mb-6 flex items-start gap-3">
                      <CalendarIcon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-slate-800">
                          Free Marketing Consultation
                        </p>
                        <p className="text-sm text-slate-500">
                          {selectedSlot
                            ? format(
                                parseISO(selectedSlot),
                                "EEEE, MMMM d, yyyy 'at' h:mm a",
                              )
                            : ""}
                        </p>
                      </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="firstName">First Name *</Label>
                          <Input
                            id="firstName"
                            name="firstName"
                            required
                            value={formData.firstName}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="lastName">Last Name *</Label>
                          <Input
                            id="lastName"
                            name="lastName"
                            required
                            value={formData.lastName}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="email">Email Address *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="phone">WhatsApp Number *</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            required
                            value={formData.phone}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="companyName">Company Name *</Label>
                        <Input
                          id="companyName"
                          name="companyName"
                          required
                          value={formData.companyName}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="notes">
                          Anything specific you want to cover?
                        </Label>
                        <Textarea
                          id="notes"
                          name="notes"
                          placeholder="e.g. I want to get more residential leads..."
                          className="resize-none h-24"
                          value={formData.notes}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="pt-4">
                        <Button
                          type="submit"
                          className="w-full bg-primary hover:bg-[#e04825] text-white h-12 text-lg"
                          disabled={submitting}
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="mr-2 h-5 w-5 animate-spin" />{" "}
                              Confirming...
                            </>
                          ) : (
                            "Confirm Booking"
                          )}
                        </Button>
                      </div>
                    </form>
                  </div>
                )}

                {step === 3 && (
                  <div className="flex flex-col items-center justify-center text-center py-12 animate-in zoom-in duration-500">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-secondary mb-2">
                      Booking Confirmed!
                    </h3>
                    <p className="text-slate-600 max-w-md mb-4">
                      Your discovery call has been scheduled for{" "}
                      {selectedSlot
                        ? format(parseISO(selectedSlot), "MMMM d 'at' h:mm a")
                        : ""}
                      .
                    </p>
                    <p className="text-slate-600 max-w-md mb-2">
                      You're about to receive a confirmation by email.
                    </p>
                    <p className="text-slate-600 max-w-md mb-8">
                      We'll also send a short video via WhatsApp with a few
                      additional questions for you to answer before the call.
                    </p>
                    <Button
                      asChild
                      className="bg-secondary hover:bg-secondary/90 text-white"
                    >
                      <Link to="/">Return to Homepage</Link>
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BookDemo;
